/**
 * Feature Flags Service
 * Main service for managing feature flags across the application
 */

import type {
  FeatureFlagProvider,
  FeatureFlags,
} from "./feature-flags.interface";
import { createLogger } from "@/services/logger/logger.service";

const logger = createLogger("FeatureFlagsService");
import { LocalStorageFeatureFlagProvider } from "./providers/local-storage.provider";
import { RuntimeConfigFeatureFlagProvider } from "./providers/runtime-config.provider";
import { HybridFeatureFlagProvider } from "./providers/hybrid.provider";
import { FEATURE_FLAG_CONFIGS } from "./feature-flags.config";
import { reactive, readonly } from "vue";

export class FeatureFlagsService {
  private provider: FeatureFlagProvider;
  private reactiveFlags: FeatureFlags;
  private changeListeners: Set<
    (key: keyof FeatureFlags, value: unknown) => void
  > = new Set();

  constructor(provider?: FeatureFlagProvider) {
    if (provider) {
      this.provider = provider;
    } else {
      // In development, use localStorage for easy testing
      // In production, use runtime config with defaults
      if (import.meta.env.DEV) {
        console.info("🏳️ Using localStorage feature flags (development mode)");
        this.provider = new LocalStorageFeatureFlagProvider();
      } else {
        console.info("🏳️ Using runtime config feature flags (production mode)");
        this.provider = new HybridFeatureFlagProvider();
      }
    }

    this.reactiveFlags = reactive(this.provider.getAll());
  }

  /**
   * Get a feature flag value
   */
  get<K extends keyof FeatureFlags>(key: K): FeatureFlags[K] {
    return this.reactiveFlags[key];
  }

  /**
   * Set a feature flag value (if provider supports it)
   */
  set<K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]): void {
    const config = FEATURE_FLAG_CONFIGS[key];

    // Validate if validator exists
    if (config.validator && !config.validator(value)) {
      logger.warn(`Invalid value for feature flag ${key}:`, value);
      return;
    }

    this.provider.set(key, value);
    this.reactiveFlags[key] = value;

    // Notify listeners
    this.changeListeners.forEach((listener) => listener(key, value));

    // Show reload message if required
    if (config.requiresReload) {
      logger.info(`Feature flag ${key} requires a page reload to take effect`);
    }
  }

  /**
   * Check if a feature is enabled
   */
  isEnabled(key: keyof FeatureFlags): boolean {
    const value = this.get(key);
    return typeof value === "boolean" ? value : false;
  }

  /**
   * Get all feature flags
   */
  getAll(): Readonly<FeatureFlags> {
    return readonly(this.reactiveFlags) as Readonly<FeatureFlags>;
  }

  /**
   * Get feature flag configuration
   */
  getConfig(key: keyof FeatureFlags) {
    return FEATURE_FLAG_CONFIGS[key];
  }

  /**
   * Get all configurations
   */
  getAllConfigs() {
    return FEATURE_FLAG_CONFIGS;
  }

  /**
   * Reset all flags to defaults
   */
  reset(): void {
    this.provider.reset();
    Object.assign(this.reactiveFlags, this.provider.getAll());
  }

  /**
   * Subscribe to feature flag changes
   */
  onChange(
    listener: (key: keyof FeatureFlags, value: unknown) => void,
  ): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  /**
   * Check if using runtime config
   */
  isUsingRuntimeConfig(): boolean {
    return this.provider instanceof RuntimeConfigFeatureFlagProvider;
  }

  /**
   * Manually refresh feature flags (no-op since no API)
   */
  async refresh(): Promise<void> {
    // No-op - feature flags come from runtime config or localStorage
    // which don't need refreshing
    return Promise.resolve();
  }
}

// Singleton instance
export const featureFlags = new FeatureFlagsService();
